/**
 * Banner Component
 *
 * Displays a CMS banner with a call-to-action, tagline, and optional action button.
 * Supports dismissal functionality through localStorage.
 */

'use client';

import { BANNER_STYLES, BannerComponentProps, BannerType, CMSBanner } from '@/@cms/types';
import { Button, Card, CardBody, Link } from '@heroui/react';
import { Icon } from '@iconify/react';
import Image from 'next/image';
import { TouchEvent, useEffect, useState } from 'react';

/**
 * Generates a unique storage key for tracking banner dismissal
 */
const getBannerStorageKey = (bannerId: number, cta: string): string => {
    return `banner_dismissed_${bannerId}_${cta.slice(0, 20).replace(/\s+/g, '_')}`;
};

/**
 * Returns the icon node for the banner, using Banner.Icon if present, else fallback
 */
const getBannerIcon = (banner: CMSBanner, iconClass: string) => {
    if (banner.Icon && banner.Icon !== 'Default') {
        // Ensure icon name doesn't already include 'lucide:' prefix
        const iconName = banner.Icon.startsWith('lucide:') ? banner.Icon : `lucide:${banner.Icon}`;
        return <Icon icon={iconName} width={60} height={65} className={iconClass} />;
    }
    return <Image src="/assets/images/megaphone.svg" alt="Announcement" width={60} height={65} />;
};

export const Banner = ({ banner, onDismiss }: BannerComponentProps) => {
    const [isVisible, setIsVisible] = useState(true);
    const [touchStart, setTouchStart] = useState<number>(0);
    const [touchEnd, setTouchEnd] = useState<number>(0);
    const minSwipeDistance = 50;

    useEffect(() => {
        if (banner.Dismissible) {
            const storageKey = getBannerStorageKey(banner.id, banner.CTA);
            const wasDismissed = localStorage.getItem(storageKey) === 'true';
            setIsVisible(!wasDismissed);
        }
    }, [banner]);

    if (!isVisible) return null;

    const handleDismiss = () => {
        if (banner.Dismissible) {
            const storageKey = getBannerStorageKey(banner.id, banner.CTA);
            localStorage.setItem(storageKey, 'true');
            setIsVisible(false);
            if (onDismiss) onDismiss();
        }
    };

    // Touch event handlers for swipe-to-dismiss on mobile
    const onTouchStart = (e: TouchEvent) => {
        setTouchEnd(0);
        setTouchStart(e.targetTouches[0].clientY);
    };
    const onTouchMove = (e: TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientY);
    };
    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        if (distance > minSwipeDistance) handleDismiss();
    };

    const hasTagline = !!banner.Tagline;
    const hasAction = banner.Action && banner.Action.Link && banner.Action.Text;
    const bannerType = banner.Type || BannerType.ANNOUNCEMENT;
    const styles = BANNER_STYLES[bannerType] || BANNER_STYLES[BannerType.ANNOUNCEMENT];
    const actionUrl = hasAction ? banner.Action!.Link : '';
    const actionText = hasAction ? banner.Action!.Text : '';

    return (
        <Card className={`relative overflow-hidden ${styles.card}`}>
            {banner.Dismissible && (
                <Button
                    isIconOnly
                    variant="light"
                    onPress={handleDismiss}
                    className={`absolute right-2 top-2 z-10 ${styles.close}`}
                    aria-label="Close banner"
                >
                    <Icon icon="lucide:x" width={20} height={20} />
                </Button>
            )}

            <CardBody
                className="relative py-4"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                <div className="flex items-center justify-between px-6 md:px-32">
                    <div className="flex items-center gap-4">
                        <div className="relative flex items-center gap-4">
                            {getBannerIcon(banner, styles.icon)}
                            <div className="flex flex-col gap-1">
                                <h2 className="text-xlarge font-semibold">{banner.CTA}</h2>
                                {hasTagline && (
                                    <p className="text-mini opacity-90">{banner.Tagline}</p>
                                )}
                            </div>
                        </div>
                    </div>
                    {hasAction && (
                        <Link
                            href={actionUrl}
                            className={`m-0 flex shrink-0 items-center gap-2 border-none p-0 ${styles.link}`}
                        >
                            {actionText}
                            <Icon icon="lucide:arrow-right" width={16} height={16} />
                        </Link>
                    )}
                </div>
            </CardBody>
        </Card>
    );
};

export default Banner;
